'use client';
import Button from '@/components/Button';
import Input from '@/components/Input';
import { Modal } from '@/components/Modal/Modal';
import ArrowLeftCircle from '@/components/icons/ArrowLeftCircle';
import PlusCircle from '@/components/icons/PlusCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import { PdvPayload, ReqAjouterPDV, useAddPdvMutation } from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import form from '@/styles/form.module.css';
import { FormInput } from '@/types';
import { getEmptyError } from '@/types/Input';
import { useContext, useEffect, useState } from 'react';
import styles from './page.module.css';

export default function Page() {
  const selectedSte = useAppSelector((state) => state.selectedSte.societe);
  const navigate = useNavigate();
  const [searchPdv, setSearchPdv] = useState<FormInput>({
    value: '',
    error_msg: '',
  });
  const [enableSearch, setEnableSearch] = useState(false);

  const [addPdv, setAddPdv] = useState<FormInput>({ value: '', error_msg: '' });
  const [libele, setLibele] = useState<FormInput>({ value: '', error_msg: '' });
  const [idExt, setIdExt] = useState<FormInput>({ value: '', error_msg: '' });
  const [enableAdd, setEnableAdd] = useState(false);

  const [ajouterPdv, { isLoading }] = useAddPdvMutation();
  const { addToast } = useContext(NotifsContext);

  const isNumeric = (str: string): boolean => {
    return /^\d*$/.test(str);
  };

  const handleAdd = async () => {
    const payload: PdvPayload = {
      bnppf_pos_id: addPdv.value,
      name: libele.value,
      partner_pos_id: idExt.value,
      employer: selectedSte.siren,
      business_domain: 'DISTRIB',
    };

    const req: ReqAjouterPDV = {
      siren: selectedSte.siren,
      reqbody: [payload],
    };

    ajouterPdv(req)
      .unwrap()
      .then(() => {
        addToast &&
          addToast({
            toastType: 'success',
            children: 'Confirmation de création.',
          });
      })
      .catch(() => {
        addToast &&
          addToast({
            toastType: 'error',
            children: `Une erreur est survenue.
          Rééssayer ultérieurement.`,
          });
      })
      .finally(() => setOpenedModal(false));
  };
  useEffect(() => {
    const toggleSearchButton = () => {
      if (searchPdv.value.length === 10 && isNumeric(searchPdv.value))
        setEnableSearch(true);
      else setEnableSearch(false);
    };
    toggleSearchButton();
  }, [searchPdv]);

  useEffect(() => {
    const toggleAddButton = () => {
      if (
        addPdv.value.length == 10 &&
        libele.value.length > 0
        /*&& idExt.value.length > 0*/
      ) {
        setEnableAdd(true);
      } else {
        setEnableAdd(false);
      }
    };
    toggleAddButton();
  }, [addPdv, idExt, libele]);

  const [openedModal, setOpenedModal] = useState(false);

  return (
    <div className={`flex-column gap-l ${form.content}`}>
      <Button
        variant="ghost"
        icon={<ArrowLeftCircle />}
        onClick={() => navigate(-1)}
      >
        Retour
      </Button>

      <form className={`${form.form} gap-l`}>
        <h3 className={form.title}>Rechercher un numéro d’agrément</h3>
        <Input
          label="Numéro agrément / union / regroupement / chaîne"
          placeholder="XXXX"
          onBlur={() => {
            if (searchPdv.value === '')
              setSearchPdv((prev) => ({ ...prev, error: true }));
            else setSearchPdv((prev) => ({ ...prev, error: false }));
          }}
          onChange={(e) => {
            if (isNumeric(e.target.value))
              setSearchPdv((prev) => ({ ...prev, value: e.target.value }));
          }}
          maxLength={10}
          value={searchPdv.value}
          helperText="10 chiffres ( Société commerciale + Agrément )"
        />
        <Button
          disabled={!enableSearch}
          onClick={(e) => {
            e.preventDefault();
            navigate('/numero-agrement/resultats/?number=' + searchPdv.value);
          }}
        >
          Rechercher
        </Button>
      </form>
      <div className={styles.ou}>
        <hr></hr>
        <p>ou</p>
        <hr></hr>
      </div>

      <form className={`${form.form} gap-l `}>
        <h3 className={form.title}>Ajouter un numéro d’agrément</h3>
        <Input
          label="Numéro agrément / union / regroupement / chaîne *"
          value={addPdv.value}
          placeholder="XXXX"
          maxLength={10}
          errorMessage={addPdv.error_msg}
          onBlur={(e) => {
            setAddPdv((prev) => ({
              ...prev,
              error_msg: getEmptyError(e.target.value),
            }));
          }}
          onChange={(e) => {
            if (isNumeric(e.target.value))
              setAddPdv((prev) => ({
                ...prev,
                value: e.target.value,
                error_msg: '',
              }));
          }}
          helperText="10 chiffres ( Société commerciale + Agrément )"
        />
        <Input
          label="Libellé *"
          placeholder="XXXX"
          maxLength={50}
          onBlur={(e) => {
            setLibele((prev) => ({
              ...prev,
              error_msg: getEmptyError(e.target.value),
            }));
          }}
          errorMessage={libele.error_msg}
          onChange={(e) => {
            setLibele((prev) => ({
              ...prev,
              value: e.target.value,
              error_msg: '',
            }));
          }}
          value={libele.value}
        />
        <Input
          label="Identifiant externe partenaire"
          placeholder="XXXX"
          maxLength={50}
          errorMessage={idExt.error_msg}
          onChange={(e) => {
            setIdExt((prev) => ({
              ...prev,
              value: e.target.value,
              error_msg: '',
            }));
          }}
          /*onBlur={(e) => {
            setIdExt((prev) => ({
              ...prev,
              error_msg: getEmptyError(e.target.value),
            }));
          }}*/
          value={idExt.value}
        />
        <p className="xs">* Champs obligatoires</p>
        <Button
          type="button"
          disabled={!enableAdd}
          onClick={(e) => {
            setOpenedModal(true);
          }}
        >
          <PlusCircle /> Ajouter un numéro d’agrément
        </Button>
      </form>
      <Modal
        primaryBtnProps={{
          loading: isLoading,
          children: 'Confirmer',
          onClick: handleAdd,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedModal(false),
        }}
        title="Voulez-vous créer le numéro d’agrément ?"
        open={openedModal}
        onClose={() => setOpenedModal(false)}
      />
    </div>
  );
}
